import React, { useEffect, useState, useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

import {
  fetchCategoryGroups,
  deleteCategoryGroup,
  searchCategoryGroups,
  clearSearchResults,
  clearError,
  setSelectedGroup,
  clearSelectedGroup,
  selectAllGroups,
  selectSearchResults,
  selectCategoryGroupsLoading,
  selectCategoryGroupsError,
} from "../reducers/categoryGroupsSlice";

import CategoryGroupTable from "./CategoryGroupTable";
import CategoryGroupModal from "./CategoryGroupModal";

export default function CategoryGroupManager() {
  const dispatch = useDispatch();

  const access_token = useSelector((state) => state.authen.accessToken);

  const groups = useSelector(selectAllGroups);

  const searchResults = useSelector(selectSearchResults);

  const loading = useSelector(selectCategoryGroupsLoading);

  const error = useSelector(selectCategoryGroupsError);

  const [searchQuery, setSearchQuery] = useState("");

  const [showModal, setShowModal] = useState(false);

  const [editingGroup, setEditingGroup] = useState(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Initial load
  useEffect(() => {
    dispatch(fetchCategoryGroups({ access_token }));
  }, [dispatch, access_token]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        dispatch(
          searchCategoryGroups({
            query: searchQuery.trim(),
            access_token,
          }),
        );
      } else {
        dispatch(clearSearchResults());
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, dispatch, access_token]);

  const displayedGroups = searchResults !== null ? searchResults : groups;

  const handleEdit = useCallback(
    (group) => {
      setEditingGroup(group);
      dispatch(setSelectedGroup(group));
      setShowModal(true);
    },
    [dispatch],
  );

  const handleCreate = useCallback(() => {
    setEditingGroup(null);
    dispatch(clearSelectedGroup());
    setShowModal(true);
  }, [dispatch]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingGroup(null);

    dispatch(clearSelectedGroup());
    dispatch(clearError());

    dispatch(fetchCategoryGroups({ access_token }));
  }, [dispatch, access_token]);

  const handleDeleteRequest = useCallback((id) => {
    setDeleteConfirmId(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId) return;

    await dispatch(
      deleteCategoryGroup({
        id: deleteConfirmId,
        access_token,
      }),
    );

    setDeleteConfirmId(null);
  }, [dispatch, deleteConfirmId, access_token]);

  return (
    <Box p={3}>
      {/* Header */}
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        justifyContent="space-between"
        alignItems={{
          xs: "flex-start",
          sm: "center",
        }}
        spacing={2}
        mb={3}
      >
        <Typography variant="h5">Pathology Category Groups</Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            New Group
          </Button>
        </Stack>
      </Stack>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={() => dispatch(clearError())}>
              <ClearIcon fontSize="small" />
            </IconButton>
          }
        >
          {typeof error === "string" ? error : JSON.stringify(error)}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            placeholder="Search group name or alias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {searchResults !== null && (
            <Typography variant="body2" color="text.secondary">
              {searchResults.length} result
              {searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <CategoryGroupTable
        groups={displayedGroups}
        loading={loading.fetch || loading.search}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
      />

      {/* Delete Dialog */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Category Group?</DialogTitle>

        <DialogContent>
          <Typography>This action cannot be undone.</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={loading.delete}
          >
            {loading.delete ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit */}
      {showModal && (
        <CategoryGroupModal group={editingGroup} onClose={handleModalClose} />
      )}
    </Box>
  );
}
